/**
 * Reconstructs how a project's whole-project tracks were split into structure
 * groups, from the `wholeProjectRoot` / `forkedFromStreamId` links the split
 * writes on `projectFileStreams`. Pure; feeds the Deliverables split diagram.
 */

import type { ProjectFileStream } from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { structureSelectionLabel, structureSelectionTitle, structureSetKey } from './structureSelectionLabel';

export interface SplitHistoryGroup {
  /** `structureSetKey` of the group. */
  key: string;
  label: string;
  /** Full comma list when the set has several structures. */
  title?: string;
  structureNames: string[];
  /** Forked tracks in this group, in root order. */
  streams: ProjectFileStream[];
}

export interface SplitHistoryEvent {
  /** When the first forked track of this split was created. */
  at: Date;
  /** Who ran the split (creator of the forked tracks). */
  byUid: string | null;
  /** Whole-project roots that were split (read-only since). */
  roots: ProjectFileStream[];
  groups: SplitHistoryGroup[];
  /**
   * The roots were already split. This cluster is a group added afterwards,
   * not another split of the whole-project tracks.
   */
  addedAfterSplit: boolean;
}

/** Forked tracks created within this window belong to one split action. */
const SAME_SPLIT_WINDOW_MS = 10 * 60 * 1000;

function time(d: Date | undefined): number {
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
}

export function buildSplitHistory(streams: ProjectFileStream[], structures: StructureRef[]): SplitHistoryEvent[] {
  const byId = new Map<string, ProjectFileStream>();
  for (const s of streams) if (s.id) byId.set(s.id, s);
  const forked = streams
    .filter(s => s.forkedFromStreamId && byId.has(s.forkedFromStreamId))
    .sort((a, b) => time(a.createdAt) - time(b.createdAt));
  if (forked.length === 0) return [];

  const names = new Map<string, string>(structures.map(s => [s.id, s.name]));
  for (const s of streams) {
    (s.structureIds ?? []).forEach((id, i) => {
      if (!names.has(id) && s.structureNames?.[i]) names.set(id, s.structureNames[i]);
    });
  }
  const rank = new Map<string, number>(structures.map((s, i) => [s.id, i]));
  const rankOf = (id: string) => rank.get(id) ?? Number.MAX_SAFE_INTEGER;

  // Cluster by creation time.
  const clusters: ProjectFileStream[][] = [];
  for (const s of forked) {
    const last = clusters[clusters.length - 1];
    if (last && time(s.createdAt) - time(last[last.length - 1].createdAt) <= SAME_SPLIT_WINDOW_MS) last.push(s);
    else clusters.push([s]);
  }

  return clusters.map(cluster => {
    const rootIds: string[] = [];
    for (const s of cluster) {
      const rid = s.forkedFromStreamId!;
      if (!rootIds.includes(rid)) rootIds.push(rid);
    }
    const roots = rootIds.map(id => byId.get(id)!);
    const rootOrder = new Map(rootIds.map((id, i) => [id, i]));

    const groups = new Map<string, SplitHistoryGroup>();
    for (const s of cluster) {
      const ids = Array.from(new Set((s.structureIds ?? []).filter(Boolean))).sort((a, b) => rankOf(a) - rankOf(b));
      const key = structureSetKey(ids);
      const setNames = ids.map(id => names.get(id) || 'Structure');
      const g = groups.get(key) ?? {
        key,
        label: (s.structureGroupLabel || '').trim() || structureSelectionLabel(setNames, { maxNamed: 3 }),
        title: structureSelectionTitle(setNames),
        structureNames: setNames,
        streams: [],
      };
      g.streams.push(s);
      groups.set(key, g);
    }
    const ordered = Array.from(groups.values());
    for (const g of ordered) {
      g.streams.sort(
        (a, b) => (rootOrder.get(a.forkedFromStreamId!) ?? 0) - (rootOrder.get(b.forkedFromStreamId!) ?? 0),
      );
    }
    ordered.sort((a, b) => {
      const ia = a.streams[0] ? (a.streams[0].structureIds ?? []) : [];
      const ib = b.streams[0] ? (b.streams[0].structureIds ?? []) : [];
      const ra = ia.length ? Math.min(...ia.map(rankOf)) : Number.MAX_SAFE_INTEGER;
      const rb = ib.length ? Math.min(...ib.map(rankOf)) : Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });

    const byUid = cluster.find(s => s.createdBy)?.createdBy ?? null;
    const start = time(cluster[0].createdAt);
    const addedAfterSplit = roots.every(root =>
      forked.some(
        s =>
          s.forkedFromStreamId === root.id &&
          time(s.createdAt) < start &&
          start - time(s.createdAt) > SAME_SPLIT_WINDOW_MS,
      ),
    );
    return { at: new Date(start), byUid, roots, groups: ordered, addedAfterSplit };
  });
}

// ── "Set up structures" events (timber-list tracks tied to proposal structures) ──

export interface StructureSetupEntry {
  stream: ProjectFileStream;
  structureNames: string[];
  /** CSV that fed the structure's timber lines. */
  fileName?: string;
  /** The shared whole-project track this version was moved out of, when applicable. */
  fromStreamTitle?: string;
  createdStructure: boolean;
}

export interface StructureSetupEvent {
  at: Date;
  byUid: string | null;
  byName: string | null;
  /** One per track, in proposal structure order. */
  entries: StructureSetupEntry[];
}

/**
 * Reconstructs "Set up structures" runs from the `structureSetup` stamp the wizard
 * writes on each timber-list track it ties to a structure. Tracks stamped within
 * {@link SAME_SPLIT_WINDOW_MS} of each other count as one run.
 */
export function buildStructureSetupHistory(streams: ProjectFileStream[], structures: StructureRef[]): StructureSetupEvent[] {
  const stamped = streams
    .filter(s => s.structureSetup && s.structureSetup.at instanceof Date)
    .sort((a, b) => time(a.structureSetup!.at) - time(b.structureSetup!.at));
  if (stamped.length === 0) return [];

  const names = new Map<string, string>(structures.map(s => [s.id, s.name]));
  for (const s of streams) {
    (s.structureIds ?? []).forEach((id, i) => {
      if (!names.has(id) && s.structureNames?.[i]) names.set(id, s.structureNames[i]);
    });
  }
  const rank = new Map<string, number>(structures.map((s, i) => [s.id, i]));
  const rankOf = (s: ProjectFileStream) => {
    const ids = (s.structureIds ?? []).filter(Boolean);
    return ids.length ? Math.min(...ids.map(id => rank.get(id) ?? Number.MAX_SAFE_INTEGER)) : Number.MAX_SAFE_INTEGER;
  };

  const clusters: ProjectFileStream[][] = [];
  for (const s of stamped) {
    const last = clusters[clusters.length - 1];
    if (last && time(s.structureSetup!.at) - time(last[last.length - 1].structureSetup!.at) <= SAME_SPLIT_WINDOW_MS) last.push(s);
    else clusters.push([s]);
  }

  return clusters.map(cluster => {
    const entries: StructureSetupEntry[] = [...cluster]
      .sort((a, b) => rankOf(a) - rankOf(b))
      .map(s => ({
        stream: s,
        structureNames: (s.structureIds ?? []).map((id, i) => names.get(id) || s.structureNames?.[i] || 'Structure'),
        fileName: s.structureSetup!.fileName,
        fromStreamTitle: s.structureSetup!.fromStreamTitle,
        createdStructure: s.structureSetup!.createdStructure,
      }));
    const first = cluster[0].structureSetup!;
    return {
      at: new Date(time(first.at)),
      byUid: first.byUid || null,
      byName: cluster.map(s => s.structureSetup!.byName).find(Boolean) ?? null,
      entries,
    };
  });
}

export type FilesStructureHistoryEvent =
  | ({ kind: 'split' } & SplitHistoryEvent)
  | ({ kind: 'structures' } & StructureSetupEvent)
  /** One "Set up structures" run that both created structures and split the other tracks. */
  | { kind: 'setup_and_split'; at: Date; byUid: string | null; setup: StructureSetupEvent; split: SplitHistoryEvent };

/**
 * Splits and structure set-ups interleaved oldest → newest, for the Deliverables history
 * card. A set-up and a split from the same run (same window, same person) are merged into
 * one `setup_and_split` event, since the wizard does both in one pass.
 */
export function buildFilesStructureHistory(streams: ProjectFileStream[], structures: StructureRef[]): FilesStructureHistoryEvent[] {
  const setups = buildStructureSetupHistory(streams, structures);
  const splits = buildSplitHistory(streams, structures);
  const usedSplits = new Set<number>();
  const events: FilesStructureHistoryEvent[] = [];
  for (const setup of setups) {
    const idx = splits.findIndex(
      (sp, i) =>
        !usedSplits.has(i) &&
        Math.abs(time(sp.at) - time(setup.at)) <= SAME_SPLIT_WINDOW_MS &&
        (!sp.byUid || !setup.byUid || sp.byUid === setup.byUid),
    );
    if (idx >= 0) {
      usedSplits.add(idx);
      const split = splits[idx];
      events.push({
        kind: 'setup_and_split',
        at: new Date(Math.min(time(setup.at), time(split.at))),
        byUid: setup.byUid ?? split.byUid,
        setup,
        split,
      });
    } else {
      events.push({ kind: 'structures', ...setup });
    }
  }
  splits.forEach((sp, i) => {
    if (!usedSplits.has(i)) events.push({ kind: 'split', ...sp });
  });
  return events.sort((a, b) => time(a.at) - time(b.at));
}
